import { CancelTokenSource } from 'axios';
import axios from 'services/http';
import { InterventionBookingStatus } from 'ts-types/api.enums';
import {
  CompleteVaccinationBookingRequestDto,
  CovidTestSampleMethod,
  EmployeeDefaultParameters,
  EmployeeTestResultImagePdfDto,
  InterventionAvailabilityDetailsDto,
  InterventionBookingHistoryDto,
  InterventionBookingStatisticsDto,
  InterventionExternalCode,
  InterventionOrderItemResultDto,
  PoolTestkitOverviewResponseDto,
  QuestionAnswerDto,
  QuestionAnswersResponseDto,
  QuestionTestResultRequest,
  SecondDoseDto,
  SendVaccinationSlotInvitationsRequest,
  TestCheckInDto,
  TestResultUpdateResponseDto,
  TreatmentBookingDto,
  TreatmentBookingUpdateRequest,
  UpsertOrderItemResultDto,
  VaccinationCheckInOutResponse,
  VerifyTestResultsInterventionRequest,
  VerifyTestResultsInterventionResponse
} from 'ts-types/api.types';

const {REACT_APP_API_BASE_URL: baseUrl} = process.env;
const apiUri = `${baseUrl}/api/intervention-booking`;

export const getInterventionBooking = (
    id: number, source: CancelTokenSource)
    : Promise<TreatmentBookingDto> => {

  return axios
      .get(`${apiUri}/${id}`, {cancelToken: source.token})
      .then((response) => response.data);
};

export const getInterventionAvailabilityForInterventionBooking = (
    id: number, source: CancelTokenSource)
    : Promise<InterventionAvailabilityDetailsDto> => {

  return axios
      .get(`${apiUri}/${id}/intervention-availability`, {cancelToken: source.token})
      .then(response => response.data)
};

export const getInterventionBookingAnswers = (
    id: number, source: CancelTokenSource)
    : Promise<Array<QuestionAnswerDto>> => {

  return axios
      .get(`${apiUri}/${id}/answers`, {cancelToken: source.token})
      .then((response) => response.data);
};

export const getInterventionBookingAnswersData = (
    id: number, source: CancelTokenSource)
    : Promise<QuestionAnswersResponseDto> => {

  return axios
      .get(`${apiUri}/${id}/answers-data`, {cancelToken: source.token})
      .then((response) => response.data);
};

export const getInterventionBookingHistory = (
    id: number, source: CancelTokenSource)
    : Promise<Array<InterventionBookingHistoryDto>> => {
  return axios
      .get(`${apiUri}/history/${id}`, {cancelToken: source.token})
      .then((response) => response.data);
};

export const getSecondDoseBookingData = (
    parentBookingId: number, source: CancelTokenSource)
    : Promise<SecondDoseDto> => {
  return axios
      .get(`${apiUri}/second-dose/${parentBookingId}`, {cancelToken: source.token})
      .then((response) => response.data);
};

export const getTestCheckInData = (
    id: number, source: CancelTokenSource)
    : Promise<TestCheckInDto> => {

  return axios
      .get(`${apiUri}/test/check-in/${id}`, {cancelToken: source.token})
      .then((response) => response.data);
};

export const getInterventionBookingDashboardStatistics = (source: CancelTokenSource)
    : Promise<InterventionBookingStatisticsDto> => {

  return axios
      .get(`${apiUri}/statistics`, {cancelToken: source.token})
      .then((response) => response.data);
};

export const getDefaultTestParametersForEmployee = (
    patientId: number,
    externalCode: InterventionExternalCode,
    source: CancelTokenSource): Promise<EmployeeDefaultParameters> => {

  return axios
      .get(`${apiUri}/${patientId}/default-parameters`, {params: {externalCode}, cancelToken: source.token})
      .then((response) => response.data);
};

export const addInterventionBooking = (
    treatmentBooking: Partial<TreatmentBookingDto>,
    source: CancelTokenSource): Promise<number> => {

  return axios
      .post(`${apiUri}`, treatmentBooking, {cancelToken: source.token})
      .then((response) => response.data);
};

export const editInterventionBooking = (
    bookingId: number,
    treatmentBooking: Partial<TreatmentBookingDto>,
    source: CancelTokenSource): Promise<string> => {

  return axios
      .put(`${apiUri}/${bookingId}`, treatmentBooking, {cancelToken: source.token})
      .then((response) => response.data);
};

export const deleteInterventionBooking = (
    bookingId: number,
    source: CancelTokenSource): Promise<string> => {

  return axios
      .delete(`${apiUri}/${bookingId}`, {cancelToken: source.token})
      .then((response) => response.data);
};

export const testCheckIn = (
    request: Partial<TreatmentBookingUpdateRequest>,
    source: CancelTokenSource): Promise<string> => {

  return axios
      .post(`${apiUri}/test/check-in`, request, {cancelToken: source.token})
      .then((response) => response.data);
};

export const updateBookingStatus = (
    bookingId: number,
    status: InterventionBookingStatus,
    source: CancelTokenSource): Promise<string> => {

  const statusString = InterventionBookingStatus[status];

  return axios
      .post(`${apiUri}/${bookingId}/status?status=${statusString}`,
          {},
          {cancelToken: source.token})
      .then((response) => response.data);
};

export const setBookingStatusOnHold = (
    bookingId: number,
    source: CancelTokenSource): Promise<InterventionBookingStatus> => {

  return axios
      .post(`${apiUri}/on-hold/${bookingId}`,
          {},
          {cancelToken: source.token})
      .then((response) => response.data);
};

export const revertBookingStatusBeforeOnHold = (
    bookingId: number,
    source: CancelTokenSource): Promise<InterventionBookingStatus> => {

  return axios
      .post(`${apiUri}/on-hold/revert/${bookingId}`,
          {},
          {cancelToken: source.token})
      .then((response) => response.data);
};

export const checkInOutVaccinationBooking = (
    bookingId: number,
    request: CompleteVaccinationBookingRequestDto,
    source: CancelTokenSource): Promise<VaccinationCheckInOutResponse> => {

  return axios
      .post(`${apiUri}/${bookingId}/check-in-out-vaccination`,
          request,
          {cancelToken: source.token})
      .then((response) => response.data);
};

export const setTestResult = (
    bookingId: number,
    treaterId: number,
    testResult: string | undefined,
    registrationTimestamp: string | undefined,
    testSampleMethod: CovidTestSampleMethod | undefined,
    rapidTestCode: string | undefined,
    ct: string | undefined,
    laboratoryId: number | undefined,
    analyzerId: number | undefined,
    remarks: string | undefined,
    source: CancelTokenSource): Promise<TestResultUpdateResponseDto> => {

  let formData = new FormData();
  formData.append("treaterId", `${treaterId}`);
  if (testResult) {
    formData.append("testResult", testResult);
  }

  if (registrationTimestamp) {
    formData.append("registrationTimestamp", registrationTimestamp);
  }

  if (testSampleMethod) {
    formData.append("testSampleMethod", testSampleMethod);
  }

  if (rapidTestCode) {
    formData.append("rapidTestCode", rapidTestCode);
  }

  if (ct) {
    formData.append("ct", ct);
  }

  if (laboratoryId) {
    formData.append("laboratoryId", `${laboratoryId}`);
  }

  if (analyzerId) {
    formData.append("analyzerId", `${analyzerId}`);
  }

  if (remarks) {
    formData.append("remarks", remarks);
  }

  return axios
      .post(`${apiUri}/${bookingId}/test-result`,
          formData,
          {cancelToken: source.token})
      .then((response) => response.data);
};

export const verifyTestResult = (
    bookingId: number,
    treaterId: number,
    testResult: string | undefined,
    registrationTimestamp: string | undefined,
    testSampleMethod: CovidTestSampleMethod | undefined,
    rapidTestCode: string | undefined,
    ct: string | undefined,
    laboratoryId: number | undefined,
    analyzerId: number | undefined,
    remarks: string | undefined,
    source: CancelTokenSource): Promise<string> => {

  let formData = new FormData();
  formData.append("treaterId", `${treaterId}`);
  if (testResult) {
    formData.append("testResult", testResult);
  }

  if (registrationTimestamp) {
    formData.append("registrationTimestamp", registrationTimestamp);
  }

  if (testSampleMethod) {
    formData.append("testSampleMethod", testSampleMethod);
  }

  if (rapidTestCode) {
    formData.append("rapidTestCode", rapidTestCode);
  }

  if (ct) {
    formData.append("ct", ct);
  }

  if (laboratoryId) {
    formData.append("laboratoryId", `${laboratoryId}`);
  }

  if (analyzerId) {
    formData.append("analyzerId", `${analyzerId}`);
  }

  if (remarks) {
    formData.append("remarks", remarks);
  }

  return axios
      .post(`${apiUri}/${bookingId}/verify-test-result`,
          formData,
          {cancelToken: source.token})
      .then((response) => response.data);
};

export const setOrderItemResult = (
    bookingId: number,
    labTestResultDto: Partial<UpsertOrderItemResultDto>,
    source: CancelTokenSource): Promise<TestResultUpdateResponseDto> => {

  return axios
      .post(`${apiUri}/${bookingId}/order-item-result`,
          labTestResultDto,
          {cancelToken: source.token})
      .then((response) => response.data);
}

export const verifyTestPanelResult = (
    bookingId: number,
    source: CancelTokenSource): Promise<string> => {

  return axios
      .post(`${apiUri}/${bookingId}/verify-test-panel-result`,
          null,
          {cancelToken: source.token})
      .then((response) => response.data);
};

export const setQuestionTestResult = (
    bookingId: number,
    request: Partial<QuestionTestResultRequest>,
    source: CancelTokenSource): Promise<TestResultUpdateResponseDto> => {

  return axios
      .post(`${apiUri}/${bookingId}/questions-test-result`,
          request,
          {cancelToken: source.token})
      .then((response) => response.data);
};

export const verifyQuestionTestResult = (
    bookingId: number,
    request: Partial<QuestionTestResultRequest>,
    source: CancelTokenSource): Promise<String> => {

  return axios
      .post(`${apiUri}/${bookingId}/verify-questions-test-result`,
          request,
          {cancelToken: source.token})
      .then((response) => response.data);
};

export const getOrderItemResult = (
    covidTestId: number,
    cancelSourceToken: CancelTokenSource): Promise<InterventionOrderItemResultDto> => {

  return axios
      .get(`${apiUri}/${covidTestId}/order-item-result`, {
        cancelToken: cancelSourceToken.token
      })
      .then(response => response.data);
}

export const invoiceInterventionBooking = (
    bookingId: number,
    treaterId: number,
    source: CancelTokenSource): Promise<string> => {

  return axios
      .post(`${apiUri}/${bookingId}/intervention-invoice`,
          null,
          {params: {treaterId: treaterId}, cancelToken: source.token})
      .then((response) => response.data);
}

export const deleteInterventionBookingInvoice = (
    bookingId: number,
    source: CancelTokenSource): Promise<string> => {

  return axios
      .delete(`${apiUri}/${bookingId}/intervention-invoice`, {cancelToken: source.token})
      .then((response) => response.data);
}

export const clearTestResult = (
    bookingId: number,
    source: CancelTokenSource): Promise<string> => {

  return axios
      .delete(`${apiUri}/${bookingId}/test-result`,
          {cancelToken: source.token})
      .then((response) => response.data);
};

export const resendEmailVerificationMail = (
    patientId: number,
    source: CancelTokenSource): Promise<number> => {

  return axios
      .post(`${apiUri}/resendEmail/${patientId}`, {cancelToken: source.token})
      .then((response) => response.data);
}

export const getTestResultImage = (
    testAttachmentId: string,
    cancelSourceToken: CancelTokenSource): Promise<EmployeeTestResultImagePdfDto> => {

  return axios
      .get(`${apiUri}/test-results/result-image/${testAttachmentId}`, {cancelToken: cancelSourceToken.token})
      .then(response => response.data);
}

export const sentVaccinationInvitationEmails = (
    source: CancelTokenSource): Promise<string> => {

  return axios
      .post(`${apiUri}/vaccinationInvitations`, {}, {cancelToken: source.token})
      .then((response) => response.data);
};

export const sendVaccinationInvitationEmailsForBookings = (
    request: Partial<SendVaccinationSlotInvitationsRequest>,
    cancelSourceToken: CancelTokenSource): Promise<number> => {

  return axios
      .post(`${apiUri}/vaccinationSlotInvitations`,
          request,
          {
            cancelToken: cancelSourceToken.token
          })
      .then(response => response.data);
};

export const sendVaccinationReminderEmailsForBookings = (
    request: Partial<SendVaccinationSlotInvitationsRequest>,
    cancelSourceToken: CancelTokenSource): Promise<number> => {

  return axios
      .post(`${apiUri}/vaccinationSlotReminders`,
          request,
          {
            cancelToken: cancelSourceToken.token
          })
      .then(response => response.data);
};

export const verifyTestResultsForInterventionBookings = (
    request: Partial<VerifyTestResultsInterventionRequest>,
    treaterId: number,
    cancelSourceToken: CancelTokenSource): Promise<VerifyTestResultsInterventionResponse> => {

  return axios
      .post(`${apiUri}/verify-multiple-test`,
          request,
          {
            params: {treaterId: treaterId},
            cancelToken: cancelSourceToken.token
          })
      .then(response => response.data);
};

export const getDetailedPoolTestKits = (
    poolTestId: number,
    cancelSourceToken: CancelTokenSource): Promise<PoolTestkitOverviewResponseDto> => {

  return axios
      .get(`${baseUrl}/api/testkit/pool-detailed/${poolTestId}`, {cancelToken: cancelSourceToken.token})
      .then(response => response.data);
};

export const getDetailedPoolTestKitsForSingulation = (
    poolTestId: number,
    cancelSourceToken: CancelTokenSource): Promise<PoolTestkitOverviewResponseDto> => {

  return axios
      .get(`${baseUrl}/api/testkit/pool-singulation/${poolTestId}`, {cancelToken: cancelSourceToken.token})
      .then(response => response.data);
}

export const populateBookingHistoryUserIds = (
    cancelSourceToken: CancelTokenSource): Promise<string> => {

  return axios
      .get(`${apiUri}/populate-history`, {cancelToken: cancelSourceToken.token})
      .then(response => response.data);
};

export const fixBookingHistoryStatus = (
    cancelSourceToken: CancelTokenSource): Promise<string> => {

  return axios
      .get(`${apiUri}/fix-history-status`, {cancelToken: cancelSourceToken.token})
      .then(response => response.data);
};

export const fixContainerEmployeeForMisantoSelfServiceBookings = (
    cancelSourceToken: CancelTokenSource): Promise<string> => {

  return axios
      .post(
          `${apiUri}/selfserviceBookingsEmployeeContainerFix`,
          undefined,
          {cancelToken: cancelSourceToken.token})
      .then(response => response.data);
};

export const fixSingleTests2021Product = (
    cancelSourceToken: CancelTokenSource): Promise<string> => {

  return axios
      .post(
          `${apiUri}/singleTests2021ProductFix`,
          undefined,
          {cancelToken: cancelSourceToken.token})
      .then(response => response.data);
};

export const fixNotInvoicedInterventionBookings = (
    cancelSourceToken: CancelTokenSource): Promise<string> => {

  return axios
      .post(
          `${apiUri}/invoiceNotInvoicedInterventionBookingsFix`,
          undefined,
          {cancelToken: cancelSourceToken.token})
      .then(response => response.data);
};

export const setControlledByBookingStatus = (bookingId: number, treaterId: number,
                                             cancelSourceToken: CancelTokenSource): Promise<TestResultUpdateResponseDto> => {

  let formData = new FormData();

  if (bookingId) {
    formData.append("bookingId", `${bookingId}`);
  }

  if (treaterId) {
    formData.append("treaterId", `${treaterId}`);
  }

  return axios
      .post(
          `${apiUri}/set-controlled-by-status`,
          formData,
          {cancelToken: cancelSourceToken.token})
      .then(response => response.data);
};

export const setResultRejectedBookingStatus = (
    bookingId: number,
    treaterId: number,
    cancelSourceToken: CancelTokenSource): Promise<string> => {

  let formData = new FormData();

  if (bookingId) {
    formData.append("bookingId", `${bookingId}`);
  }

  if (treaterId) {
    formData.append("treaterId", `${treaterId}`);
  }

  return axios
      .post(
          `${apiUri}/set-result-rejected-status`,
          formData,
          {cancelToken: cancelSourceToken.token})
      .then(response => response.data);
};

export const resendTestResultEmail = (
    bookingId: number,
    cancelSourceToken: CancelTokenSource)
    : Promise<TestResultUpdateResponseDto> => {

  let formData = new FormData();
  formData.append("bookingId", `${bookingId}`);

  return axios
      .post(
          `${apiUri}/resend-test-result-email`,
          formData,
          {cancelToken: cancelSourceToken.token})
      .then(response => response.data);

}
